import React from 'react';
import { motion } from 'framer-motion';
import { ClipboardList, Truck, CheckCircle, ArrowRight } from 'lucide-react';

const steps = [
  {
    icon: ClipboardList,
    step: '01',
    title: 'Book Your Shipment',
    desc: 'Fill in your pickup location, drop destination, vehicle type, cargo details and preferred date. Our smart booking system takes less than 2 minutes to complete.',
    color: '#FF6B00',
  },
  {
    icon: Truck,
    step: '02',
    title: 'We Assign & Dispatch',
    desc: 'Our operations team instantly assigns the best-fit vehicle from our premium fleet and dispatches it to your location with a confirmed driver and tracking ID.',
    color: '#3b82f6',
  },
  {
    icon: CheckCircle,
    step: '03',
    title: 'Safe Delivery',
    desc: 'Your cargo is delivered on time, fully insured, with real-time GPS updates and delivery confirmation sent directly to your WhatsApp number.',
    color: '#22c55e',
  },
];

const HowItWorks = () => (
  <section className="section-padding bg-white relative overflow-hidden">
    <div className="absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-brand-orange/5 blur-[120px] rounded-full pointer-events-none" />
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
      className="absolute -bottom-32 -left-32 w-96 h-96 border border-brand-orange/5 rounded-full pointer-events-none"
    />
    <motion.div
      animate={{ rotate: -360 }}
      transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
      className="absolute -bottom-20 -left-20 w-64 h-64 border border-brand-orange/8 rounded-full pointer-events-none"
    />

    <div className="max-w-7xl mx-auto relative z-10">
      {/* Header */}
      <div className="text-center mb-12 sm:mb-16 md:mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-2 bg-brand-orange/5 border border-brand-orange/20 rounded-full px-4 sm:px-5 py-2 mb-5 sm:mb-6"
        >
          <motion.div
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-2 h-2 rounded-full bg-brand-orange"
          />
          <span className="text-brand-orange font-black tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-xs uppercase">Simple Process</span>
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-brand-black tracking-tighter mb-4"
        >
          HOW IT <span className="text-brand-orange">WORKS</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-brand-muted max-w-xl mx-auto text-sm sm:text-base px-4"
        >
          Three simple steps to get your cargo moving across India with full tracking and insurance coverage.
        </motion.p>
      </div>

      {/* Steps */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-8 relative">
        {/* Connector line — desktop only */}
        <div className="hidden md:block absolute top-14 left-[22%] right-[22%] h-[2px] overflow-hidden">
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.4, ease: 'easeOut' }}
            style={{ originX: 0 }}
            className="h-full bg-gradient-to-r from-brand-orange/30 via-brand-orange to-brand-orange/30"
          />
        </div>

        {steps.map((s, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.18, type: 'spring', stiffness: 80 }}
            whileHover={{ y: -10 }}
            className="relative z-10 flex flex-col items-center text-center group"
          >
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 3 + idx, repeat: Infinity, ease: 'easeInOut' }}
              className="relative mb-5 sm:mb-6"
            >
              <motion.div
                animate={{ scale: [1, 1.4, 1], opacity: [0.4, 0, 0.4] }}
                transition={{ duration: 2.5, repeat: Infinity, delay: idx * 0.4 }}
                className="absolute inset-0 rounded-full"
                style={{ background: `${s.color}20` }}
              />
              <div
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-full flex items-center justify-center relative transition-all duration-500 group-hover:scale-110"
                style={{ background: `${s.color}12`, border: `2px solid ${s.color}30` }}
              >
                <s.icon size={34} style={{ color: s.color }} />
                <motion.span
                  whileHover={{ scale: 1.2, rotate: 10 }}
                  className="absolute -top-2 -right-2 w-8 h-8 rounded-full text-white text-[10px] font-black flex items-center justify-center shadow-lg"
                  style={{ background: s.color }}
                >
                  {s.step}
                </motion.span>
              </div>
            </motion.div>

            {/* Arrow between steps — mobile */}
            {idx < steps.length - 1 && (
              <motion.div
                animate={{ y: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="md:hidden mb-4"
              >
                <ArrowRight size={20} className="text-brand-orange/40 rotate-90" />
              </motion.div>
            )}

            <h3 className="text-lg sm:text-xl font-black text-brand-black mb-2 sm:mb-3 group-hover:text-brand-orange transition-colors duration-300">{s.title}</h3>
            <p className="text-brand-muted text-sm leading-relaxed max-w-xs px-2">{s.desc}</p>

            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: '3rem' }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.2 + 0.5 }}
              className="h-[2px] mt-4 rounded-full"
              style={{ background: s.color }}
            />
          </motion.div>
        ))}
      </div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 }}
        className="text-center mt-12 sm:mt-16"
      >
        <motion.a
          href="#book"
          onClick={(e) => { e.preventDefault(); document.getElementById('book')?.scrollIntoView({ behavior: 'smooth' }); }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
          className="inline-flex items-center gap-3 bg-brand-orange text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full font-black tracking-widest text-xs sm:text-sm hover:bg-brand-orange-light transition-all orange-glow"
        >
          START BOOKING NOW
          <motion.span animate={{ x: [0, 4, 0] }} transition={{ duration: 1.2, repeat: Infinity }}>
            <ArrowRight size={18} />
          </motion.span>
        </motion.a>
      </motion.div>
    </div>
  </section>
);

export default HowItWorks;
